
import { isoRange, rangeLabel, timeTextLabel } from '../utils/shiftTime';
import { createPortal } from 'react-dom';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CheckCircle2, X, AlertCircle, Siren, Circle, Send, Loader2, Phone, Clock, Plus } from 'lucide-react';
import Modal from '../components/ui/Modal';
import Dialog, { DialogType } from '../components/ui/Dialog';
import { api, Shift, getJSTDate, getJSTDateString, Absence, Staff, createJSTDate, Store, DefaultShiftSetting } from '../api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';

interface ShiftRequest {
  date: string;
  type: 'preset' | 'custom' | 'off' | null;
  presetLabel?: string;
  startTime?: string;
  endTime?: string;
  isSubmitted: boolean;
  preference?: 'circle' | 'triangle';
}

interface Preset { label: string; time: string; color: string; id: number; positionId?: string; }

const UserShiftPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { currentUser: authUser } = useAuth();
  const { userProfile: contextUserProfile, currentStore: contextStore } = useUser();
  const [currentDate, setCurrentDate] = useState(getJSTDate());
  const [requests, setRequests] = useState<Record<string, ShiftRequest>>({});
  const [confirmedShifts, setConfirmedShifts] = useState<Record<string, Shift>>({});
  const [submittedRequests, setSubmittedRequests] = useState<Record<string, Shift>>({}); 
  const [selectedDateStr, setSelectedDateStr] = useState<string | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<Staff | null>(null);
  const [userStore, setUserStore] = useState<Store | null>(null);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [ownerPhone, setOwnerPhone] = useState<string | null>(null);
  // 追加募集・打診（自動作成で埋まらなかった枠への応募）
  const [openShifts, setOpenShifts] = useState<Array<{ vacancy: Shift; store: Store | null; offered: boolean; applied: boolean }>>([]);
  const [positionNames, setPositionNames] = useState<Record<string, string>>({});
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const refreshOpenShifts = (staffId: string) => { api.getOpenShiftsForStaff(staffId).then(setOpenShifts).catch(() => {}); };
  const handleApply = async (vacancyId: string) => {
      if (!currentUser) return;
      setApplyingId(vacancyId);
      try {
          await api.applyForOpenShift(vacancyId, currentUser.id);
          refreshOpenShifts(currentUser.id);
      } catch (e) { showToast('応募に失敗しました。', 'error'); }
      finally { setApplyingId(null); }
  };
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialog, setDialog] = useState<{isOpen: boolean, type: DialogType, title: string, message: string, onConfirm: () => void}>({
      isOpen: false, type: 'info', title: '', message: '', onConfirm: () => {}
  });

  const shiftsUnsubRef = useRef<(() => void) | null>(null);

  const loadData = async () => {
      // AuthContext/UserContextからユーザーIDを取得
      const userId = authUser?.uid || contextUserProfile?.id || null;
      if (userId) {
          const u = { id: userId };
          setCurrentUser(u);
          const stores = await api.getStores();
          const allStaff = await api.getStaff();
          const myProfile = allStaff.find(s => s.id === userId);
          setUserProfile(myProfile || null);

          if (stores.length > 0) {
              const store = stores[0];
              setUserStore(store);
              const owner = allStaff.find(s => s.id === store.ownerId);
              if (owner?.phoneNumber) setOwnerPhone(owner.phoneNumber);
          }
          
          api.getPositions().then(ps => setPositionNames(Object.fromEntries(ps.map(p => [p.id, p.name])))).catch(() => {});
          // 月を切り替えるたびに購読が増えないよう、前の購読を解除してから購読する
          shiftsUnsubRef.current?.();
          shiftsUnsubRef.current = api.subscribeShifts(u.id, (shifts) => {
              refreshOpenShifts(u.id);
              const confirmedMap: Record<string, Shift> = {};
              const requestMap: Record<string, Shift> = {};
              shifts.forEach(s => {
                  const dateKey = s.start.split('T')[0];
                  if (s.confirmed || s.type === 'fixed') confirmedMap[dateKey] = s;
                  else if (s.type === 'request' || s.type === 'off') requestMap[dateKey] = s;
              });
              setConfirmedShifts(confirmedMap);
              setSubmittedRequests(requestMap);
          });

          try {
              const types = await api.getShiftTypes();
              const colors = ['bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700', 'bg-orange-100 text-orange-700'];
              // 希望として選べるのは勤務の区分のみ（有給・欠勤などは除く）。
              const validPresets = types.filter(t => (!t.category || t.category === 'work' || t.category === 'holiday_work')).filter(t => !t.positionIds || t.positionIds.length === 0 || myProfile?.skills?.some(s => t.positionIds?.includes(s.positionId) && s.level > 0));
              const mappedPresets = validPresets.map((t, i) => ({ id: t.id, label: t.name, time: t.time, color: colors[i % colors.length], positionId: t.positionIds?.[0] }));
              setPresets(mappedPresets);

              // オーナーの電話番号を取得
              if (stores.length > 0) {
                const owner = allStaff.find(s => s.id === stores[0].ownerId);
                if (owner?.phoneNumber) setOwnerPhone(owner.phoneNumber);
              }

              // デフォルト設定の自動読み込み
              if (stores.length > 0) {
                const defaults = await api.getDefaultSettings(userId, stores[0].id);
                if (defaults.length > 0) {
                  const autoRequests: Record<string, ShiftRequest> = {};
                  const targetDate = currentDate;
                  const daysInMonth = Array.from({ length: 31 }, (_, i) => createJSTDate(targetDate.getFullYear(), targetDate.getMonth() + 1, i + 1)).filter(d => d.getMonth() === targetDate.getMonth());
                  daysInMonth.forEach(date => {
                    const dateStr = getJSTDateString(date);
                    if (dateStr < getJSTDateString()) return; // 過去日はスキップ
                    const dow = date.getDay(); // 0=日, 1=月, ...
                    const defaultSetting = defaults.find(d => d.dayOfWeek === dow);
                    if (defaultSetting && defaultSetting.type !== 'off') {
                      const preset = defaultSetting.presetId ? mappedPresets.find(p => p.id === defaultSetting.presetId) : null;
                      autoRequests[dateStr] = {
                        date: dateStr,
                        type: defaultSetting.type === 'preset' ? 'preset' : 'custom',
                        isSubmitted: false,
                        presetLabel: preset?.label,
                        startTime: defaultSetting.type === 'preset' ? preset?.time.split('-')[0] : defaultSetting.startTime,
                        endTime: defaultSetting.type === 'preset' ? preset?.time.split('-')[1] : defaultSetting.endTime,
                      };
                    }
                  });
                  if (Object.keys(autoRequests).length > 0) {
                    setRequests(autoRequests);
                  }
                }
              }
          } catch(e) { console.error(e); }
      }
  };

  const currentMonth = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
  useEffect(() => { loadData(); }, [authUser?.uid, contextUserProfile?.id, currentMonth]);
  // 画面を離れたら（ログアウト時を含む）購読を解除する
  useEffect(() => () => { shiftsUnsubRef.current?.(); shiftsUnsubRef.current = null; }, []);

  const formatDate = (d: Date) => getJSTDateString(d);
  const todayStr = getJSTDateString();
  const days = Array.from({ length: 31 }, (_, i) => createJSTDate(currentDate.getFullYear(), currentDate.getMonth() + 1, i + 1)).filter(d => d.getMonth() === currentDate.getMonth());

  const handleDateClick = (dateStr: string) => {
      if (dateStr < todayStr && !confirmedShifts[dateStr]) return;
      setSelectedDateStr(dateStr);
      setIsSheetOpen(true);
  };

  const handleAbsenceRequest = (shift: Shift) => {
      setDialog({
          isOpen: true, type: 'confirm', title: '欠勤申請', message: '本当に欠勤申請をしますか？\n申請すると、直ちに管理者へ通知され、代わりのスタッフを探すSOSが自動送信されます。',
          onConfirm: async () => {
              setIsSubmitting(true);
              setDialog(prev => ({ ...prev, isOpen: false }));
              try {
                  await api.reportAbsence(shift.id);
                  showToast('欠勤申請を完了しました。');
                  setIsSheetOpen(false);
              } catch (e) { showToast('申請に失敗しました。', 'error'); } 
              finally { setIsSubmitting(false); }
          }
      });
  };

  const handleRequestUpdate = (type: 'preset' | 'custom' | 'off' | null, preference?: 'circle' | 'triangle', preset?: Preset) => {
      if (!selectedDateStr) return;
      if (type === null) {
          const n = {...requests}; delete n[selectedDateStr]; setRequests(n);
      } else {
          setRequests(prev => ({ ...prev, [selectedDateStr]: {
              date: selectedDateStr, type, isSubmitted: false, preference,
              ...(type === 'preset' ? { presetLabel: preset?.label, startTime: preset?.time.split('-')[0], endTime: preset?.time.split('-')[1] } : {}),
              ...(type === 'off' ? { presetLabel: '休み希望' } : {})
          }}));
      }
      setIsSheetOpen(false);
  };

  // 提出期限チェック：表示中の月の希望は「前月の提出期限日」まで提出できる。
  // （例：期限20日なら、11月分は10月20日まで。当月以前の分は期限の対象外）
  const isDeadlinePassed = (() => {
    if (!userStore?.submitDeadline) return false;
    const now = getJSTDate();
    const deadline = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, userStore.submitDeadline, 23, 59, 59);
    const isFutureMonth = currentDate.getFullYear() * 12 + currentDate.getMonth() > now.getFullYear() * 12 + now.getMonth();
    return isFutureMonth && now > deadline;
  })();

  const handleSubmit = async () => {
      if (!currentUser || !userStore) return;
      if (isDeadlinePassed) {
        showToast(`提出期限（毎月${userStore.submitDeadline}日）を過ぎています。`, 'error');
        return;
      }
      setIsSubmitting(true);
      try {
          const payload: Shift[] = Object.values(requests).map((req) => ({
              id: `req_${Date.now()}_${Math.random()}`, staffId: currentUser.id, store: userStore.id,
              // 終了が開始以前なら翌日の終了（深夜営業）
              start: req.type === 'off' ? `${req.date}T00:00:00` : isoRange(req.date, req.startTime!, req.endTime!).start,
              end: req.type === 'off' ? `${req.date}T23:59:59` : isoRange(req.date, req.startTime!, req.endTime!).end,
              type: req.type === 'off' ? 'off' : 'request', label: req.presetLabel || '希望', confirmed: false, preference: req.preference
          }));
          if (payload.length > 0) await api.saveShifts(payload);
          setIsSubmitModalOpen(false); setRequests({});
          showToast('シフト希望を提出しました。');
      } catch (e) { showToast('提出に失敗しました。', 'error'); } 
      finally { setIsSubmitting(false); }
  };

  const countRequested = Object.values(requests).length;

  return (
    <div className="animate-in pb-24">
      <Dialog {...dialog} onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))} />
      
      <div className="bg-white sticky top-0 z-20 shadow-sm border-b border-gray-100">
          <div className="flex items-center justify-between px-6 py-4">
              <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth()-1, 1))} className="p-2 bg-gray-50 rounded-full"><ChevronLeft size={20}/></button>
              <h2 className="text-lg font-bold text-slate-800">{currentDate.getFullYear()}年 {currentDate.getMonth()+1}月</h2>
              <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth()+1, 1))} className="p-2 bg-gray-50 rounded-full"><ChevronRight size={20}/></button>
          </div>
      </div>

      {isDeadlinePassed && userStore?.submitDeadline && (
        <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
          <AlertCircle size={16} className="text-red-500 shrink-0" />
          <p className="text-xs font-bold text-red-600">提出期限（毎月{userStore.submitDeadline}日）を過ぎています。新規提出はできません。</p>
        </div>
      )}

      {openShifts.length > 0 && (
          <div className="mx-4 mt-3 p-4 bg-orange-50/60 border border-orange-200 rounded-2xl" data-testid="open-shifts">
              <p className="text-sm font-black text-slate-800 mb-1">追加募集・打診</p>
              <p className="text-[11px] text-slate-500 mb-3">人手が足りないシフトです。入れる場合は応募してください。</p>
              <ul className="space-y-2">
                  {openShifts.map(({ vacancy: v, store, offered, applied }) => {
                      const d = new Date(Number(v.start.slice(0, 4)), Number(v.start.slice(5, 7)) - 1, Number(v.start.slice(8, 10)));
                      return (
                          <li key={v.id} className="bg-white border border-orange-100 rounded-xl px-3 py-2.5 flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                  <div className="text-sm font-bold text-slate-800 whitespace-nowrap">{d.getMonth() + 1}月{d.getDate()}日（{['日','月','火','水','木','金','土'][d.getDay()]}）{rangeLabel(v)}</div>
                                  <div className="text-[11px] text-slate-500 truncate">{positionNames[v.positionId || ''] || ''}{store && store.id !== userStore?.id ? `（${store.name}）` : ''}{offered ? '　あなたへの打診' : ''}</div>
                              </div>
                              {applied ? (
                                  <span className="shrink-0 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 whitespace-nowrap">応募済み</span>
                              ) : (
                                  <button type="button" disabled={applyingId === v.id} onClick={() => handleApply(v.id)} className="shrink-0 text-xs font-bold text-white bg-orange-500 rounded-lg px-3 py-1.5 whitespace-nowrap">応募する</button>
                              )}
                          </li>
                      );
                  })}
              </ul>
          </div>
      )}

      <div className="p-4 space-y-3">
          {days.map(date => {
              const dateStr = formatDate(date);
              const confirmed = confirmedShifts[dateStr]; 
              const submitted = submittedRequests[dateStr]; 
              const req = requests[dateStr];
              return (
                  <div key={dateStr} onClick={() => handleDateClick(dateStr)} className={`relative bg-white rounded-2xl p-4 flex items-center justify-between border ${confirmed ? 'bg-blue-50/50 border-blue-100' : 'border-gray-100'} shadow-sm active:scale-98 transition-all`}>
                      <div className="flex items-center">
                          {/* 日付と曜日を横並びに変更 */}
                          <div className={`px-3 py-2 rounded-xl flex items-baseline justify-center mr-4 min-w-[70px] ${date.getDay()===0 ? 'text-red-500 bg-red-50' : date.getDay()===6 ? 'text-blue-500 bg-blue-50' : 'text-slate-700 bg-gray-50'}`}>
                              <span className="text-xl font-black leading-none">{date.getDate()}</span>
                              <span className="text-xs font-bold ml-1">({['日','月','火','水','木','金','土'][date.getDay()]})</span>
                          </div>
                          <div>
                              {confirmed ? (
                                  <div className="flex flex-col">
                                      <span className="text-xs font-bold text-blue-600 mb-1 flex items-center"><CheckCircle2 size={12} className="mr-1"/> 確定</span>
                                      <span className="text-lg font-bold text-slate-800">{rangeLabel(confirmed)}</span>
                                  </div>
                              ) : req ? (
                                  <span className="text-sm font-bold text-blue-500 italic">未送信の希望あり</span>
                              ) : submitted ? (
                                  <span className="text-xs font-bold text-yellow-600 flex items-center"><Clock size={12} className="mr-1"/> 申請中</span>
                              ) : <span className="text-sm font-bold text-slate-300">未提出</span>}
                          </div>
                      </div>
                  </div>
              );
          })}
      </div>

      {countRequested > 0 && createPortal(
          <div className="fixed bottom-20 inset-x-0 mx-auto w-full max-w-[440px] px-6 z-30 flex justify-center">
              <button onClick={() => setIsSubmitModalOpen(true)} className="w-full animate-slide-up bg-slate-800 text-white py-4 rounded-2xl shadow-xl font-bold text-lg flex items-center justify-center"><Send className="mr-2" size={20} /> 提出する（{countRequested}）</button>
          </div>,
          document.body
      )}

      {isSheetOpen && createPortal(
          <div className="fixed inset-0 z-50 flex flex-col justify-end">
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsSheetOpen(false)} />
              <div className="relative bg-white rounded-t-3xl p-6 shadow-2xl animate-slide-up max-h-[85vh] overflow-y-auto">
                  <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mb-8"></div>
                  {selectedDateStr && confirmedShifts[selectedDateStr] ? (
                      <div className="space-y-6">
                          <div className="text-center">
                              <h3 className="text-xl font-bold text-slate-800 mb-2">確定シフトの詳細</h3>
                              <p className="text-2xl font-black text-blue-600">{rangeLabel(confirmedShifts[selectedDateStr])}</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                              <h4 className="text-xs font-bold text-slate-400 mb-3 uppercase tracking-wider">欠勤について</h4>
                              {userStore?.allowDigitalAbsence ? (
                                  <div className="space-y-4">
                                      <p className="text-xs text-slate-600 leading-relaxed font-bold">急な欠勤が必要な場合は、以下のボタンから申請してください。即座に管理者へ通知され、代わりのスタッフを探すSOSが自動送信されます。</p>
                                      <button onClick={() => handleAbsenceRequest(confirmedShifts[selectedDateStr!])} className="w-full py-4 bg-red-50 text-red-600 font-bold rounded-xl border border-red-200 flex items-center justify-center gap-2 hover:bg-red-100 transition-colors"><Siren size={20} /> 欠勤申請（SOS通知を飛ばす）</button>
                                  </div>
                              ) : (
                                  <div className="space-y-4 text-center">
                                      <p className="text-sm text-red-600 font-black leading-relaxed p-4 bg-red-50 rounded-xl border border-red-100 flex flex-col items-center gap-2">
                                          <AlertCircle size={24} /> 欠勤の際は必ず責任者に直接連絡してください。
                                      </p>
                                      {ownerPhone && <a href={`tel:${ownerPhone}`} className="inline-flex items-center text-blue-600 font-bold border-b border-blue-600 pb-0.5"><Phone size={14} className="mr-1" /> 責任者へ電話する</a>}
                                  </div>
                              )}
                          </div>
                      </div>
                  ) : (
                      <div className="space-y-4">
                          <h3 className="text-lg font-bold text-center mb-6">{selectedDateStr?.split('-')[2]}日の希望を選択</h3>
                          {presets.map(p => (
                              <button key={p.id} onClick={() => handleRequestUpdate('preset', 'circle', p)} className="w-full bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex items-center justify-between hover:border-blue-500 transition-all">
                                  <div><div className={`text-xs font-bold px-2 py-0.5 rounded w-fit mb-1 ${p.color}`}>{p.label}</div><div className="font-bold text-slate-700">{timeTextLabel(p.time)}</div></div>
                                  <span className="text-blue-500 border border-blue-100 p-1 rounded-full"><Plus size={20} /></span>
                              </button>
                          ))}
                          <button onClick={() => handleRequestUpdate('off')} className="w-full p-4 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center font-bold text-slate-500"><X size={18} className="mr-2"/> 休み希望</button>
                      </div>
                  )}
              </div>
          </div>,
          document.body
      )}

      <Modal isOpen={isSubmitModalOpen} onClose={() => setIsSubmitModalOpen(false)} title="提出確認" maxWidth="max-w-xs">
          <div className="text-center py-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-4">{isSubmitting ? <Loader2 className="animate-spin" size={24} /> : <Send size={24} />}</div>
              <h3 className="font-bold text-lg text-slate-800 mb-6">{countRequested}日分の希望を送信します。</h3>
              <button onClick={handleSubmit} disabled={isSubmitting} className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl shadow-lg">送信する</button>
          </div>
      </Modal>
    </div>
  );
};

export default UserShiftPage;
